// src/components/GardenDrawController.tsx
/**
 * Main controller component for the 'Suntap' garden drawing interface.
 * Coordinates between 3D viewer, drawing tools, and garden visualisation.
 * Acts as the parent component that manages the 3D scene and delegates
 * interactions to the appropriate subcomponents. Handles canvas click events,
 * garden selection, and provides methods for polygon drawing while maintaining
 * the relationship between the ThreeJsViewer and garden-specific functionality.
 */
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HeightIcon from '@mui/icons-material/Height';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { trackGardenEditStarted, trackGardenDeleted, trackUncertaintyToggled } from '../../utils/analytics';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import ThreeJsViewer, { ThreeJsViewerProps, ThreeJsViewerRef, FocalPoint } from './ThreeJsViewer';
import GardenDrawingTool, { GardenDrawingToolRef } from './GardenDrawingTool'; // Added DrawingState import
import GardenVisualisation from './GardenVisualisation';
import { Garden } from '../../types/gardenTypes';

// Re-export FocalPoint for convenience
export type { FocalPoint };

// Define a base type without the properties we want to override
type GardenDrawControllerPropsBase = Omit<ThreeJsViewerProps, 'onCanvasClick' | 'onMeshesLoaded' | 'onGardenSelect'>;

// Define props for the controller component
export interface GardenDrawControllerProps extends GardenDrawControllerPropsBase {
  onPolygonComplete?: (coordinates: [number, number, number][], gardenName?: string, isUncertain?: boolean) => void;
  onGardenDelete?: (gardenUid: string) => void;
  onToggleUncertainty?: (garden: Garden, isUncertain: boolean) => void;
  preservePolygon?: boolean;
  handleGardenNaming?: boolean;
  showInitialHelp?: boolean;
  googlePlaceId?: string;
  // Note: onGardenSelect from the Viewer (focal points) is different from the one below
  // This onGardenSelect is triggered when a VISUALIZED garden polygon is clicked
  onGardenSelect?: (garden: Garden) => void; // Callback when a visualized garden is selected
  onPubHasNoGarden?: () => void; // Callback to mark the pub as having no garden
  pubHasNoGarden?: boolean; // New prop to indicate if pub is marked as having no garden
  onGardensLoaded?: (gardens: Garden[]) => void; // Add this line to forward the callback
  reload?: number;
}

/**
 * Main controller component that manages the 3D viewer and coordinates
 * between garden drawing and visualisation subcomponents
 */
const GardenDrawController: React.FC<GardenDrawControllerProps> = ({
  meshUris,
  initialZoom,
  focalPoint,
  focalPoints,
  skipRaycasting,
  pubName,
  height = '600px',
  onGardenSelect, // Renamed from prop to avoid conflict with internal handler name
  onGardenDelete,
  onToggleUncertainty,
  onPolygonComplete,
  onPubHasNoGarden,
  onGardensLoaded, // Add this prop to destructuring
  pubHasNoGarden = false, // Default to false if not provided
  preservePolygon = false,
  handleGardenNaming = false,
  showInitialHelp = true,
  googlePlaceId,
  reload = 0,
}) => {
  // Reference to the ThreeJsViewer
  const viewerRef = useRef<ThreeJsViewerRef>(null);
  // Reference to the GardenDrawingTool to call its methods
  const drawingToolRef = useRef<GardenDrawingToolRef>(null);

  // State to track if the viewer has loaded its essentials
  const [viewerIsReady, setViewerIsReady] = useState(false);
  // State to track the garden selected from the visualisation for potential editing
  const [selectedGardenForEdit, setSelectedGardenForEdit] = useState<Garden | null>(null);
  // State to track if gardens should be reloaded
  const [reloadGardens, setReloadGardens] = useState<number>(0);
  // State for confirmation dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showUncertaintyDialog, setShowUncertaintyDialog] = useState<boolean>(false);
  
  // New state for the "pub has garden" modal
  const [showPubHasGardenModal, setShowPubHasGardenModal] = useState<boolean>(false);
  
  const elevationStep = 0.1; // Step size in meters

  // State to track the elevation adjustment for the garden
  const [gardenBeingAdjustedUid, setGardenBeingAdjustedUid] = useState<string | null>(null);


  // Callback when meshes are loaded in ThreeJsViewer
  const handleMeshesLoaded = useCallback(() => {
    // Use a minimal timeout to increase the chance that the refs within
    // ThreeJsViewer (exposed via useImperativeHandle) are fully populated
    // after the async loading and setup logic completes.
    const timer = setTimeout(() => {
        setViewerIsReady(true);
    }, 100); // 100ms delay, adjust if needed

    // Cleanup timer if component unmounts before timeout
    return () => clearTimeout(timer);
  }, []);


  // Canvas click handler - pass to the drawing tool component via its ref
  const handleViewerCanvasClick = useCallback((event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => {
    // Check if the click was on an existing garden visual handled by GardenVisualisation
    // We can check intersection.object.userData, but GardenVisualisation already has its own click handler.
    // Let GardenVisualisation handle its clicks and call `handleGardenSelectedFromVisualisation`.
    // Delegate non-garden clicks to the drawing tool.
    // Check if the intersection object belongs to GardenVisualisation group
    let isGardenVisualClick = false;
    if (intersection?.object) {
        let currentObject: THREE.Object3D | null = intersection.object;
        while(currentObject) {
            if (currentObject.name === 'GardensVisualisationGroup') {
                isGardenVisualClick = true;
                break;
            }
            currentObject = currentObject.parent;
        }
    }

    // If it's not a click on an existing garden visual, pass it to the drawing tool
    if (!isGardenVisualClick && drawingToolRef.current) {
        drawingToolRef.current.handleCanvasClick(event, intersection, raycaster);
    }
  }, []); // No dependencies needed, relies on ref current value

  // Check if pub is marked as "no garden" when it's loaded
  useEffect(() => {
    if (pubHasNoGarden && googlePlaceId) {
      setShowPubHasGardenModal(true);
    }
  }, [pubHasNoGarden, googlePlaceId]);

  // Wrap onPolygonComplete to trigger a reload after a garden is saved
  const handlePolygonComplete = useCallback((coordinates: [number, number, number][], gardenName?: string, isUncertain?: boolean) => {
    // Check if this is an edit operation (gardenName includes "::")
    const isEditOperation = gardenName && gardenName.includes('::');

    // Call the original callback
    if (onPolygonComplete) {
      onPolygonComplete(coordinates, gardenName, isUncertain);
    }

    // Clear any drawing visuals that might be lingering
    // Make sure drawingToolRef and clearVisuals exist before calling
    if (drawingToolRef.current && typeof drawingToolRef.current.clearVisuals === 'function') {
        // Short delay might still be needed if clearVisuals is async or depends on state updates
        setTimeout(() => {
            if (drawingToolRef.current && typeof drawingToolRef.current.clearVisuals === 'function') {
                drawingToolRef.current.clearVisuals();
            }
        }, 100);
    }

    // Reset the adjustment state tracking
    setGardenBeingAdjustedUid(null);

    // Increment reloadGardens to trigger a reload
    setTimeout(() => {
      setReloadGardens(prev => prev + 1);
      console.log("Triggering garden reload to show updated garden");
    }, 800);
}, [onPolygonComplete]);

  // Callback for when a garden is selected *from the GardenVisualisation component*
  const handleGardenSelectedFromVisualisation = useCallback((garden: Garden) => {
    // Check if the drawing tool is currently busy (drawing, naming, etc.)
    let isDrawingToolBusy = false;
    if (drawingToolRef.current && typeof drawingToolRef.current.isBusy === 'function') {
        isDrawingToolBusy = drawingToolRef.current.isBusy();
    }

    if (isDrawingToolBusy) {
        // Optionally provide user feedback here
        return;
    }

    setSelectedGardenForEdit(garden); // Set the selected garden for potential editing

    // Call the optional external callback if provided
    if (onGardenSelect) {
      onGardenSelect(garden);
    }
  }, [onGardenSelect]); // Depends on the external callback

  // Callback for when a garden is deselected
  const handleGardenDeselected = useCallback(() => {
    setSelectedGardenForEdit(null); // Clear the selected garden when deselected
  }, []);

  // Handler for "Draw New Garden" button click (the + icon)
  const startNewDrawing = useCallback(() => {
    if (drawingToolRef.current) {
      console.log('GardenDrawController: Initiating new garden drawing');
      // Clear any selected garden
      setSelectedGardenForEdit(null);
      // Call the drawing tool's method to start drawing
      if (typeof drawingToolRef.current.startDrawing === 'function') {
        drawingToolRef.current.startDrawing();
      }
    } else {
      console.warn("Start Drawing clicked but drawing tool not ready.");
    }
  }, []);

  // Track the number of gardens loaded
  const [gardenCount, setGardenCount] = useState<number>(0);
  
  // Handler for when gardens are loaded by GardenVisualisation
  const handleGardensLoaded = useCallback((gardens: Garden[]) => {
    // console.log(`GardenDrawController: Visualisation loaded ${gardens.length} gardens`);
    // Store the garden count to determine whether to show the "no garden" link
    setGardenCount(gardens.length);
    
    // Forward to external callback if provided
    if (onGardensLoaded) {
      onGardensLoaded(gardens);
    }
  }, [onGardensLoaded]);

  // Handler for the 'Edit Garden' button click
  const handleEditGardenClick = useCallback(() => {
    if (selectedGardenForEdit && drawingToolRef.current) {
      console.log(`GardenDrawController: Initiating edit for garden: ${selectedGardenForEdit.name || selectedGardenForEdit.uid}`);
      
      trackGardenEditStarted(googlePlaceId || '', gardenCount);
      
      // Existing code continues...
      drawingToolRef.current.startEditMode(selectedGardenForEdit);
      setSelectedGardenForEdit(null);
      
      // IMPORTANT FIX: Tell GardenVisualisation to deselect the garden so it returns to orange
      if (viewerRef.current && viewerRef.current.scene) {
        const gardensGroup = viewerRef.current.scene.getObjectByName('GardensVisualisationGroup');
        if (gardensGroup) {
          // Trigger deselection in the visualization component
          if (handleGardenDeselected) {
            handleGardenDeselected();
          }
        }
      }
    } else {
        console.warn("Edit Garden clicked but no garden selected or drawing tool not ready.");
    }
  }, [selectedGardenForEdit, handleGardenDeselected]); // Added handleGardenDeselected to dependencies

  // Handler for the 'Adjust Height' button click
  const handleAdjustHeightClick = useCallback(() => {
    if (selectedGardenForEdit && drawingToolRef.current) {
        console.log(`GardenDrawController: Initiating height adjustment for garden: ${selectedGardenForEdit.name || selectedGardenForEdit.uid}`);

        // <<< ADDED: Set the UID of the garden being adjusted
        setGardenBeingAdjustedUid(selectedGardenForEdit.uid);

        // Call the drawing tool's method to start the height adjustment process
        drawingToolRef.current.startHeightAdjustment(selectedGardenForEdit);

        // Clear the selection state in the controller, the button will disappear.
        // We keep track via gardenBeingAdjustedUid now, not selection state.
        setSelectedGardenForEdit(null);

    } else {
      console.warn("Adjust Height clicked but no garden selected or drawing tool not ready.");
    }
}, [selectedGardenForEdit]);

const handleDrawingCancel = useCallback(() => {
  console.log("GardenDrawController: Drawing/Editing Cancelled");
  setGardenBeingAdjustedUid(null); // Reset adjustment state on cancel
}, []);

  // Handler to confirm garden deletion
  const confirmDeleteGarden = useCallback(() => {
    if (selectedGardenForEdit && onGardenDelete) {
      // analytics
      trackGardenDeleted(googlePlaceId || '', selectedGardenForEdit.uid);

      onGardenDelete(selectedGardenForEdit.uid);
      
      // Reset selection state
      setSelectedGardenForEdit(null);
      
      // Increment reload counter to refresh gardens after deletion
      setTimeout(() => {
        setReloadGardens(prev => prev + 1);
        console.log("Triggering garden reload after deletion");
      }, 800);
    }
    setShowDeleteDialog(false);
  }, [selectedGardenForEdit, onGardenDelete]);
  
  // Handler for the 'Toggle Uncertainty' button click
  const handleUncertaintyClick = useCallback(() => {
    if (selectedGardenForEdit) {
      setShowUncertaintyDialog(true);
    }
  }, [selectedGardenForEdit]);
  
  // Handler to confirm uncertainty toggle
  const confirmToggleUncertainty = useCallback(() => {
    if (selectedGardenForEdit && onToggleUncertainty) {
      // Toggle the current uncertainty status
      const newUncertaintyStatus = !selectedGardenForEdit.is_uncertain;
      trackUncertaintyToggled(selectedGardenForEdit.uid, newUncertaintyStatus);
      onToggleUncertainty(selectedGardenForEdit, newUncertaintyStatus);
      
      // Increment reload counter to refresh gardens after update
      setTimeout(() => {
        setReloadGardens(prev => prev + 1);
        console.log("Triggering garden reload after uncertainty update");
      }, 800);
    }
    setShowUncertaintyDialog(false);
  }, [selectedGardenForEdit, onToggleUncertainty]);

  // Extract viewer properties safely within the render function
  // These will be undefined/null initially, and populated after viewer is ready
  const viewerProps = viewerRef.current;
  const scene = viewerProps?.scene;
  const container = viewerProps?.container;
  const controls = viewerProps?.controls;
  const trueUpVector = viewerProps?.trueUpVector;

  // Determine if we can render children based on state and current ref values
  const canRenderChildren = viewerIsReady && !!scene && !!container && !!controls && !!trueUpVector;

  // Log the state for debugging
  useEffect(() => {
  }, [viewerIsReady, scene, container, controls, trueUpVector, canRenderChildren]); // Log when these values change

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      {/* Main 3D Viewer */}
      <ThreeJsViewer
        ref={viewerRef} // Pass the viewer ref
        meshUris={meshUris}
        initialZoom={initialZoom}
        focalPoint={focalPoint}
        focalPoints={focalPoints}
        skipRaycasting={skipRaycasting}
        pubName={pubName}
        height="100%"
        onMeshesLoaded={handleMeshesLoaded} // Use the handler to set state
        onGardenSelect={(index: number, name: string) => { // This is for FOCAL POINT selection
          setSelectedGardenForEdit(null); // Deselect any visualized garden if a focal point is clicked
        }}
        onCanvasClick={handleViewerCanvasClick} // Pass the controller's click handler
      />

      {/* Conditionally render children only when viewer state is ready AND refs are available */}
      {canRenderChildren && viewerProps && (
        <>
          {/* Garden Drawing Tool - Pass guaranteed non-null props */}
          <GardenDrawingTool
            ref={drawingToolRef} // Pass the drawing tool ref
            scene={scene!} // Use non-null assertion as check passed
            container={container!}
            controls={controls!}
            trueUpVector={trueUpVector!}
            onPolygonComplete={handlePolygonComplete} // Pass the wrapped callback
            preservePolygon={preservePolygon}
            handleGardenNaming={handleGardenNaming}
            showInitialHelp={showInitialHelp}
            googlePlaceId={googlePlaceId}
            onFocusOnPoint={(point) => viewerProps.focusOnPoint(point)} // Call method directly
            onEnterPlanView={() => viewerProps.enterPlanView()}
            onExitPlanView={() => viewerProps.exitPlanView()}
            onCancel={handleDrawingCancel} // <<< ADDED: Pass the cancel handler
        />

          {/* Garden Visualisation - Pass guaranteed non-null props */}
          <GardenVisualisation
            scene={scene!}
            container={container!}
            trueUpVector={trueUpVector!}
            googlePlaceId={googlePlaceId}
            reload={reload}
            onGardenSelect={handleGardenSelectedFromVisualisation}
            onGardenDeselect={handleGardenDeselected}
            onGardensLoaded={handleGardensLoaded}
            gardenBeingAdjustedUid={gardenBeingAdjustedUid}
          />

          {/* Contextual Buttons - Rendered again here ensures drawingToolRef is available */}
           {selectedGardenForEdit ? (
              <Box sx={{
                  position: 'absolute',
                  top: 8, // Adjust position as needed
                  left: 8, // Position at the left side
                  zIndex: 1010, // Ensure it's above drawing tool buttons
                  pointerEvents: 'auto', // Make sure it's clickable
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1, // Space between buttons
              }}>
                  <Tooltip title={`Edit ${selectedGardenForEdit.name || 'Garden'}`}>
                      <IconButton
                          color="primary"
                          onClick={handleEditGardenClick}
                          sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                          size="medium"
                      >
                          <EditIcon />
                      </IconButton>
                  </Tooltip>
                  <Tooltip title={`Adjust Height`}>
                      <IconButton
                          color="primary"
                          onClick={handleAdjustHeightClick}
                          sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                          size="medium"
                      >
                          <HeightIcon />
                      </IconButton>
                  </Tooltip>
                  {/* Uncertainty Toggle Button */}
                  <Tooltip title={selectedGardenForEdit.is_uncertain ? 
                      `Mark as Certain` : 
                      `Mark as Uncertain`}>
                      <IconButton
                          color={selectedGardenForEdit.is_uncertain ? "warning" : "primary"}
                          onClick={handleUncertaintyClick}
                          sx={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.7)', 
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } 
                          }}
                          size="medium"
                      >
                          <HelpOutlineIcon />
                      </IconButton>
                  </Tooltip>
                  {/* Delete Garden Button */}
                  <Tooltip title={`Delete`}>
                  <IconButton
                      color="error"
                      onClick={confirmDeleteGarden} // Use the correct deletion handler
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                      }}
                      size="medium"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
              </Box>
           ) : (
              // Only show "Draw New Garden" button when not in elevation editing mode
              !gardenBeingAdjustedUid && (
                <Box sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1010,
                    pointerEvents: 'auto',
                }}>
                    <Tooltip title="Draw New Area">
                        <IconButton
                            color="primary"
                            onClick={startNewDrawing}
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 1)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}
                            size="medium"
                        >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
              )
           )}
           
           {/* "No Garden" Text Link at the bottom - only show if there are no gardens */}
           {onPubHasNoGarden && gardenCount === 0 && (
             <Box sx={{
               position: 'absolute',
               bottom: 10, // Aligned with Google logo
               right: 10, // Right side positioning
               maxWidth: '50%', // Constrain to right half of screen
               zIndex: 1010,
               pointerEvents: 'auto',
               textAlign: 'right', // Align text to the right
             }}>
               <Typography 
                 variant="body2"
                 component="span" 
                 onClick={onPubHasNoGarden}
                 sx={{
                   color: 'rgba(100, 100, 100, 0.8)',
                   fontStyle: 'italic',
                   textDecoration: 'underline',
                   cursor: 'pointer',
                   padding: '4px 8px',
                   borderRadius: '4px',
                   backgroundColor: 'rgba(255, 255, 255, 0.6)',
                   display: 'inline-block', // Allow it to wrap
                   wordWrap: 'break-word', // Enable word wrapping
                   '&:hover': {
                     backgroundColor: 'rgba(255, 255, 255, 0.8)',
                     color: 'rgba(50, 50, 50, 0.9)',
                   },
                 }}
               >
                 {pubHasNoGarden ? "This pub has no outdoor space" : "Does this pub have no outdoor space?"}
               </Typography>
             </Box>
           )}

          {/* Delete Garden Confirmation Dialog */}
          <Dialog
            open={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            aria-labelledby="delete-garden-dialog-title"
          >
            <DialogTitle id="delete-garden-dialog-title">Confirm Deletion</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete the garden "{selectedGardenForEdit?.name || 'Selected Garden'}"? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button onClick={confirmDeleteGarden} color="error">Delete</Button>
            </DialogActions>
          </Dialog>
          
          {/* Uncertainty Toggle Confirmation Dialog */}
          <Dialog
            open={showUncertaintyDialog}
            onClose={() => setShowUncertaintyDialog(false)}
            aria-labelledby="uncertainty-dialog-title"
          >
            <DialogTitle id="uncertainty-dialog-title">
              {selectedGardenForEdit?.is_uncertain ? 
                "Mark as Certain" : 
                "Mark as Uncertain"}
            </DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                {selectedGardenForEdit?.is_uncertain ? 
                  `Are you sure you want to mark the garden "${selectedGardenForEdit?.name || 'Selected Garden'}" as certain?` : 
                  `Are you sure you want to mark the garden "${selectedGardenForEdit?.name || 'Selected Garden'}" as uncertain?`}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {selectedGardenForEdit?.is_uncertain ? 
                  "This indicates you're confident in the garden boundaries." : 
                  "This indicates you're not fully confident in the garden boundaries."}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowUncertaintyDialog(false)}>Cancel</Button>
              <Button onClick={confirmToggleUncertainty} color={selectedGardenForEdit?.is_uncertain ? "primary" : "warning"}>
                {selectedGardenForEdit?.is_uncertain ? "Mark as Certain" : "Mark as Uncertain"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* "Pub Has Garden" Dialog */}
          <Dialog
            open={showPubHasGardenModal}
            onClose={() => setShowPubHasGardenModal(false)}
            aria-labelledby="pub-has-garden-dialog-title"
          >
            <DialogTitle id="pub-has-garden-dialog-title">
              This pub was marked as having no garden
            </DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                Someone previously marked this pub as having no outdoor space. If you believe this pub does have a garden or outdoor area, you can draw it using the tools on this page.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Drawing a garden will automatically update the pub's status in our database.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPubHasGardenModal(false)} color="primary">
                Got it
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

// Export alias for backward compatibility
const GardenDraw3D = GardenDrawController;
export default GardenDraw3D;